import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { SortDirection } from '../sort-direction';
import { Observable, Subscription } from 'rxjs';
import { SearchObjectResult } from '../search-object-result.model';
import { LoaderService } from '../services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { DataFiltersService } from '../data-filters/data-filters.service';
import { TranslateService } from '@ngx-translate/core';
import { DataFiltersComponent } from '../data-filters/data-filters.component';
import { finalize } from 'rxjs/operators';
import { ErrorHandlerService } from '../services/error-handler.service';

export interface DataTableColumn {
  name: string;
  displayed: boolean;
  translationKey: string;
  template: TemplateRef<any>;
  sortable: boolean;
  exportable?: boolean;
  alignRight?: boolean;
  alignCenter?: boolean;
  widthCol?: number;
  overflowVisible?: boolean;
  selectColumn?: boolean;
  onSelect?: (checked: boolean) => void;
  notHideable?: boolean;
  customTitleTemplate?: TemplateRef<any>;
}

export interface DataTableSubrow {
  name: string;
  template: TemplateRef<any>;
}

export interface DataTableItem {
  subrows?: DataTableItemSubrows;
}

export type DataTableItemSubrows = {[subrowName: string]: DataTableItemSubrow};

export interface DataTableItemSubrow {
  displayed: boolean;
}

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() columns: DataTableColumn[] = [];
  @Input() sortColumn: DataTableColumn;
  @Input() direction = SortDirection.DESC;
  @Input() loadPage: (page: number, pageSize: number, sortColumn: DataTableColumn, direction: SortDirection)
    => Observable<SearchObjectResult<any>>;
  @Input() filters: any = {};
  @Input() headSticky = false;
  @Input() infiniteScroll = false;
  @Input() noOverflow = false;
  @Input() pageSize = 10;
  @Input() filtersComponent: DataFiltersComponent;
  @Input() showColumnsSelection = true;
  @Input() allowItemSelection = false;
  @Input() allowMultiItemSelection = false;
  @Input() loadAtStartup = true;
  @Input() lightSkin = false;
  @Input() selectOnRowClick = true;
  @Input() loadOnReset = true;
  @Input() cleanOnReset = false;
  @Input() hidePageSelector = false;
  @Input() disableSearch = false;
  @Input() additionalRowTemplate: TemplateRef<any>;
  @Input() additionalInfoKey: string;
  @Input() noResultLabelKey = 'data-table.no-result';
  @Input() loadObservable: Observable<number>;
  @Output() filtersChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() beforeReset = new EventEmitter<void>();
  @Output() onReset: EventEmitter<void> = new EventEmitter<void>();
  @Output() onLoad = new EventEmitter<SearchObjectResult<any>>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onItemSelection = new EventEmitter<any[] | any>();
  @Output() onColumnSelected = new EventEmitter<DataTableColumn>();

  items: any[] = [];
  selectedItems: string[] = [];
  count           = 0;
  totalCount      = 0;
  page            = 1;
  lastPage        = 1;
  pageSizeOptions = [10, 20, 30, 40, 50];
  SortDirection = SortDirection;
  resetSubscription: Subscription;
  applySubscription: Subscription;
  loadingSubscription: Subscription;
  loading = false;

  constructor(
    private loader: LoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private filtersService: DataFiltersService,
    private errorHandler: ErrorHandlerService,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngAfterViewInit(): void {
    this.resetSubscription = this.filtersService.resetObservable.subscribe(() => this.reset());
    this.applySubscription = this.filtersService.applyObservable.subscribe(() => this.refresh());
    if (this.loadObservable) {
      this.loadingSubscription = this.loadObservable.subscribe(page => this.changePage(page ? page : this.page));
    }
  }

  refresh() {
    this.changePage(1);
  }

  reset() {
    this.beforeReset.emit();

    this.filters = {};
    this.filtersChange.emit(this.filters);
    this.onReset.emit();

    if (this.loadOnReset && !this.disableSearch) {
      this.refresh();
    } else if (this.cleanOnReset) {
      this.items = [];
      this.selectedItems = [];
      this.count = 0;
      this.totalCount = 0;
      this.page = 1;
      this.lastPage = 1;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadPage && changes.loadPage.currentValue && this.loadAtStartup) {
      this.changePage(1);
    }

    if (changes.filtersComponent && changes.filtersComponent.currentValue) {
      this.resetSubscription = this.filtersComponent.getResetObservable().subscribe(() => this.reset());
      this.applySubscription = this.filtersComponent.getApplyObservable().subscribe(() => this.refresh());
    }
  }

  changePage(page: number, autoSelectFirstItem = false) {
    if (!this.loadPage) {
      return;
    }

    this.page = page;
    this.onPageChange.emit(this.page);

    this.selectItem(null);

    this.loader.startLoad();

    this.loadPage(this.page, this.pageSize, this.sortColumn, this.direction)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(res => {
        this.count      = res.count;
        this.totalCount = res.totalCount;
        this.items      = res.results;
        this.lastPage   = res.totalPages;

        this.onLoad.emit(res);
         this.cdr.detectChanges();

        if (autoSelectFirstItem) {
          this.selectFirstItem();
        }
      }, e => this.errorHandler.handleHttpError(e, 'data-table.errors.load-page'));
  }

  loadNextPage() {
    if (!this.loadPage) {
      return;
    }

    if (this.loading) {
      console.log('Already loading a page');
      return;
    }

    this.page = this.page + 1;
    this.onPageChange.emit(this.page);

    this.loader.startLoad();
    this.loading = true;

    this.loadPage(this.page, this.pageSize, this.sortColumn, this.direction)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.loading = false;
      }))
      .subscribe(res => {
        this.items.push(...res.results);

        this.onLoad.emit(res);
      }, e => this.errorHandler.handleHttpError(e, 'data-table.errors.load-page'));
  }

  onScroll() {
    this.loadNextPage();
  }

  sort(column: DataTableColumn) {
    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.direction  = SortDirection.ASC;
    } else if (this.direction === SortDirection.DESC) {
      this.direction = SortDirection.ASC;
    } else {
      this.direction = SortDirection.DESC;
    }

    if (!this.disableSearch) {
      this.refresh();
    }
  }

  selectItem(selectedItem?: any) {
    if (selectedItem == null) {
      this.selectedItems = [];
    } else if (this.allowMultiItemSelection) {
      this.selectMultiItem(selectedItem);
      this.onItemSelection.emit(this.selectedItems);
    } else if (this.allowItemSelection) {
      this.selectSingleItem(selectedItem);
      this.onItemSelection.emit(this.selectedItems[0]);
    }
  }

  selectFirstItem() {
    if (this.items.length > 0) {
      this.selectItem(this.items[0]);
    }
  }

  ngOnDestroy() {
    this.resetSubscription.unsubscribe();
    this.applySubscription.unsubscribe();
    if (this.loadObservable) {
      this.loadingSubscription.unsubscribe();
    }
  }

  onColumnFilterChange(column: DataTableColumn) {
    this.onColumnSelected.emit(column);
  }

  public isSelected(item: any) {
    return this.selectedItems.includes(item);
  }

  public selectSingleItem(item: any) {
    if (this.selectedItems.includes(item)) {
      this.selectedItems = [];
    } else {
      this.selectedItems = [item];
    }
  }

  public selectMultiItem(item: any) {
    const selectedItemIndex = this.selectedItems.indexOf(item);
    if (selectedItemIndex === -1) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = [
        ...this.selectedItems.slice(0, selectedItemIndex),
        ...this.selectedItems.slice(selectedItemIndex + 1, this.selectedItems.length )
      ];
    }
  }
}
